import FaqComponentView from "../faq/faqComponentView";
import MainCTA from "../CTA-sections/mainCTA";
import {setupFAQs} from "../../static-data/allFAQs";
import {Image1} from "../../assets";

export default function ResearchExplainerComponent(props){

    function imageComponent(imageSrc) {
        return (
            <div className="flex justify-center">
                <img
                    className="rounded w-2/3"
                    src={imageSrc}/>
            </div>
        )
    }

    return (
        <div>
            <main>
                <section className="container container-wide container-single">
                    <section id="extended-text" className="ns-bio-hero alignfull">
                        <div className="container ns-bio-hero__info">
                            <div className="ns-bio-hero__info__content">
                                <h1 className="p1">
                                    <div className="text-5xl sm:text-8xl">SEO Strategy & Research</div>
                                </h1>
                                <p className="p1" style={{'fontSize': '20px'}}>
                                    <strong>TL;DR:</strong> Define your goals (KPIs like traffic, sales, or brand recognition) and
                                    use three core strategies to
                                    achieve them cost-effectively: precise keyword research (targeting easy wins and
                                    high-value terms),
                                    technical monitoring, and competitive research.
                                </p>

                            </div>
                        </div>
                        <div className="ns-bio-hero__image">
                            <figure>
                                <img decoding="async"
                                     src={Image1}
                                     alt="SEO Woman arms crossed thinking about conversion rate optimization strategies for your website."/>
                            </figure>
                        </div>
                    </section>
                    <h2 className="wp-block-heading text-4xl sm:text-6xl">
                        <strong>RESEARCH STEP: IDENTIFY ECONOMICAL KPIs AND LOWER COSTS</strong></h2>
                    <p>
                        Before researching any SEO strategy, it's good idea to define your key performance indicators
                        (KPIs).
                        These metrics will guide your efforts and help you measure success. Think about what you really
                        want
                        to get out of your SEO efforts. Do you want more people finding your website? Do you want your
                        brand to be more well-known?
                    </p>
                    <p>
                        Common KPIs include:
                        <li><strong>Organic Traffic:</strong>&nbsp;Increase website traffic from search engines to drive
                            engagement and boost sales.</li>
                        <li><strong>Stronger Website:</strong>&nbsp;Build those valuable backlinks that show Google
                            you're a trusted source.
                        </li>
                        <li><strong>Brand Recognition:</strong>&nbsp;Show up more often in search results, so people
                            start to recognize your name.
                        </li>
                        <li><strong>Keyword Tracking:</strong>&nbsp;Understand where your website ranks for important
                            words, so you can see what's working and what's not.
                        </li>
                    </p>
                    <p>
                        Things like <i>how long people stay on your site</i> and <i>how quickly it</i> loads are
                        important too. And of course, the most important thing is to see your sales numbers go up –
                        that's the real goal.
                        By knowing what you want to achieve, you can track your progress and make sure your SEO efforts
                        are actually paying off!
                    </p>

                    <p>
                        {imageComponent("https://firebasestorage.googleapis.com/v0/b/pabloo-inc.appspot.com/o/SEO%2FGoogle-search-console.png?alt=media&token=5a8cb801-3499-4e2e-9973-1a0c21279e5c")}
                        <div>
                            <strong>TIP:</strong> As part of your SEO efforts, focus on keyword research, on-page
                            optimization, and high quality
                            content creation. These strategies will help you attract more visitors and improve your
                            site's
                            visibility in search engine results. Additionally, leveraging tools like Google Search
                            Console
                            and Bing Webmaster Tools will enable you to monitor your site's performance and make
                            necessary
                            adjustments.
                        </div>
                    </p>

                    <h2 className="wp-block-heading text-4xl sm:text-6xl">
                        <strong>RANK TRACKING & BACKLINK ANALYSIS</strong>&nbsp;&nbsp;</h2>
                    <p>
                        Next step, we'll set up our tools for rank tracking and backlink analysis. Our rank tracking
                        tool
                        will monitor your rankings in search results and help us understand if the rest of your SEO
                        efforts are working.
                    </p>
                    <p>
                        Our backlink analysis tool is incredibly powerful. It uses the largest and most up-to-date
                        backlink checker on the market, giving you the inside scoop on who's linking to you and how
                        those links are impacting your search rankings.
                        High-quality backlinks are essential for improving your website's search engine rankings. They
                        act as votes of confidence, telling search engines that your website is trustworthy and
                        valuable. However, low-quality or spammy backlinks can harm your rankings and even get you
                        penalized by Google.
                    </p>
                    <div>
                        <strong>NOTE:</strong>&nbsp;Imagine your website's online reputation is like your social circle.
                        Backlinks are like the friends
                        your website hangs out with. Analyzing them helps you understand who your website's friends are.
                        Are they the cool kids with a good reputation, or are they troublemakers that could get you in
                        trouble?
                    </div>
                    {imageComponent("https://firebasestorage.googleapis.com/v0/b/pabloo-inc.appspot.com/o/SEO%2Fbing-webmaster-tools-performance.png?alt=media&token=8accf535-25ba-46f8-b58d-d277b3c3477c")}
                    <div>
                        <strong>TIP:</strong>&nbsp;Keep an eye out for special offers. Bing frequently provides ad
                        credits to promote their search engine.
                    </div>


                    <h2 className="wp-block-heading text-4xl sm:text-6xl">
                        <strong>CONDUCT KEYWORD RESEARCH</strong>&nbsp;&nbsp;</h2>
                    <p>
                        Our keyword research process identifies:
                        <ol>
                            <li key={0}>
                                <strong>Low-Competition Keywords:</strong>&nbsp;We'll uncover keywords with minimal
                                competition, offering a higher chance of ranking quickly.
                            </li>
                            <li key={1}>
                                <strong>Long-Tail Keywords:</strong>&nbsp;Utilizing "Google Suggest," we'll identify
                                highly specific long-tail keywords. While these terms may have lower search volumes,
                                they often result in higher conversion rates due to their targeted nature.
                            </li>
                            <li key={2}>
                                <strong>"Money" Keywords:</strong>&nbsp;We'll target high-volume, high-competition
                                keywords directly related to your products or services, which have the potential for
                                significant traffic and revenue.
                            </li>

                            Finally, we'll create a comprehensive keyword map, strategically assigning relevant keywords
                            to specific pages on your website."

                        </ol>
                        Conducting keyword research allows us to identify low competition keywords, which you can easily
                        win.
                        Next, we will identify Long tail keyword variations with "Google Suggest". Which are highly
                        specific search queries. These terms tend to have relatively low search volumes, however, they
                        make up a large percentage of overall searches and can have higher conversion rates.
                        We will also find your main "Money" keywords. Typically, these are typically high-volume and
                        high-competition terms related to your products, services, or other areas of specialty.
                        Finally, we create a keyword map, to map the keywords to pages on your site.

                    </p>
                    {imageComponent("https://firebasestorage.googleapis.com/v0/b/pabloo-inc.appspot.com/o/SEO%2Fga-vs-gs.webp?alt=media&token=654dbf2d-e56d-42d8-889e-d4a44a3c7287")}
                    <div>
                        <strong>TIP:</strong>&nbsp;Keep an eye out for special offers. Bing frequently provides ad
                        credits to promote their search engine.
                    </div>


                    <h2 className="wp-block-heading text-4xl sm:text-6xl">
                        <strong>COMPETITIVE RESEARCH: SPY ON COMPETITORS AND UNCOVER MARKET OPPORTUNITIES &
                            THREATS</strong>&nbsp;&nbsp;</h2>
                    <p>
                        We'll see what keywords your competitors are ranking for, who's linking to their website, and
                        how they're doing in search.
                        We can learn a lot from their successes and use that to our advantage.

                    </p>
                    {imageComponent("https://firebasestorage.googleapis.com/v0/b/pabloo-inc.appspot.com/o/SEO%2Fga-vs-gs.webp?alt=media&token=654dbf2d-e56d-42d8-889e-d4a44a3c7287")}
                    <div>
                        <strong>TIP:</strong>&nbsp;Keep an eye out for special offers. Bing frequently provides ad
                        credits to promote their search engine.
                    </div>
                    <br/>

                    <div className="my-12 mb-24">
                        <MainCTA/>
                    </div>

                    <h3 className="custom-font font-semibold">Frequently Asked Questions</h3>
                    <FaqComponentView faqData={setupFAQs}/>

                </section>
            </main>
        </div>
    )
}